import { Link } from 'expo-router';
import { router } from 'expo-router';
import { jwtDecode } from 'jwt-decode';
import { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { OtpInput } from 'react-native-otp-entry';

import { Button, IconMail, IconRefresh, Text, tokens } from '@fhs/ui';

import { IdentifyActionSheet } from '../components/actions-sheets/identify-action-sheet';
import { AuthIdentifiers, IdentifyAuthJwt, useAuthV2Context } from '../context';
import { formatPhoneNumber } from '../utils';

const OTP_LENGTH = 6;

export function VerifyOtp() {
  const {
    createOtp,
    validateOtp,
    resendOtp,
    resendOtpDisabled,
    showIdentifyPopup,
    setShowIdentifyPopup,
    identifyAuthJwts,
    getJwtIdentifier,
    routeWithJWTContext,
  } = useAuthV2Context();

  const attemptValidateCode = useCallback(
    async otpInput => {
      await validateOtp(otpInput);
    },
    [validateOtp]
  );

  const getIdentifierComponent = useCallback((loginIdentifier, identifierType) => {
    switch (identifierType) {
      case AuthIdentifiers.PHONE_NUMBER:
        //Format as phone
        return <Text weight="bold">{formatPhoneNumber(loginIdentifier)} </Text>;
      case AuthIdentifiers.EMAIL:
        return <Text weight="bold">{loginIdentifier} </Text>;
      case AuthIdentifiers.JWT:
        return (
          <Text weight="bold">
            {jwtDecode<IdentifyAuthJwt>(loginIdentifier).redactedIdentity.redactedEmail}
          </Text>
        );
    }
  }, []);

  const getLinkComponent = useCallback(
    identifierType => {
      switch (identifierType) {
        case AuthIdentifiers.PHONE_NUMBER:
          return (
            <Link href={'/v2/signin'} style={{ textDecorationLine: 'underline' }}>
              Change Phone
            </Link>
          );
        case AuthIdentifiers.EMAIL:
        case AuthIdentifiers.JWT:
          return (
            <Button
              type="ghost"
              size="xs"
              hoveredStyle={{ backgroundColor: tokens.colors.$white }}
              style={{ alignSelf: 'flex-start' }}
              onPress={() => routeWithJWTContext('/v2/signin/(continuewithemail)')}
            >
              <Button.Text style={{ textDecorationLine: 'underline' }}>Change Email</Button.Text>
            </Button>
          );
      }
    },
    [routeWithJWTContext]
  );

  const { loginIdentifier, identifierType } = getJwtIdentifier();

  return (
    <>
      <Text.Heading type={'three'}>Verify With Code </Text.Heading>
      <Text>
        {'Enter the 6-digit code sent to you at '}
        {getIdentifierComponent(loginIdentifier, identifierType)}
      </Text>
      {getLinkComponent(identifierType)}
      <OtpInput
        numberOfDigits={OTP_LENGTH}
        focusColor={tokens.colors.$blackOpacity30}
        autoFocus
        blurOnFilled
        type="numeric"
        onFilled={attemptValidateCode}
        theme={{
          containerStyle: styles.otpContainer,
          pinCodeContainerStyle: styles.inputBox,
          filledPinCodeContainerStyle: styles.inputBox,
        }}
        textInputProps={{
          accessibilityLabel: 'One-Time Password',
        }}
        textProps={{
          accessibilityRole: 'text',
          accessibilityLabel: 'OTP digit',
          allowFontScaling: false,
          style: styles.otpText,
        }}
      />
      <View style={styles.buttonContainer}>
        <Button
          style={styles.button}
          type={'outline'}
          size="sm"
          onPress={resendOtp}
          disabled={resendOtpDisabled}
        >
          <Button.Icon>
            <IconRefresh style={styles.buttonContent} />
          </Button.Icon>
          <Button.Text style={styles.buttonContent}>Resend OTP</Button.Text>
        </Button>
        <Button
          style={styles.button}
          type={'outline'}
          size="sm"
          onPress={() => {
            router.push('/v2/signin/(continuewithemail)');
          }}
        >
          <Button.Icon>
            <IconMail style={styles.buttonContent} />
          </Button.Icon>
          <Button.Text style={styles.buttonContent}>Verify With Email</Button.Text>
        </Button>
      </View>
      {showIdentifyPopup && (
        <IdentifyActionSheet
          authJwts={identifyAuthJwts}
          isVisible={showIdentifyPopup}
          onClose={() => {
            setShowIdentifyPopup(false);
          }}
          onRedirect={routeWithJWTContext}
          onSelect={(jwt: string) => {
            createOtp({
              loginIdentifier: jwt,
              identifierType: AuthIdentifiers.JWT,
            });
          }}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  button: {
    alignItems: 'center',
    marginVertical: 12,
    borderColor: tokens.colors.$blackOpacity75,
    textDecorationColor: tokens.colors.$black,
  },
  buttonContainer: {
    alignItems: 'flex-start',
  },
  buttonContent: {
    alignItems: 'center',
    color: tokens.colors.$black,
  },
  inputBox: {
    width: 36,
    height: 48,
    textAlign: 'center',
    color: tokens.colors.$black,
    borderColor: tokens.colors.$blackOpacity30,
    borderWidth: 1,
    borderRadius: 0,
  },
  otpContainer: {
    width: 'auto',
    height: 100,
    gap: 12,
    paddingVertical: 28,
    alignContent: 'center',
  },
  otpText: {
    fontFamily: 'Montserrat',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: 400,
  },
});
